import type { ReactNode } from 'react';
import './RenjuRulePaper.css';

type Point = readonly [number, number];
type Stone = { at: Point; white?: boolean; last?: boolean };
type BoardProps = {
  name: string; rows: number; cols: number; unit: number; start: Point; stones: Stone[];
  targets?: Point[]; scope?: number; groups?: Point[][]; label: string;
};
const BLACK = '#292c28', WHITE = '#fffef8';
const P = (at: Point, white = false, last = false): Stone => ({at, white, last});
function RenjuBoardPicture({name, rows, cols, unit, start, stones, targets = [], scope, groups = [], label}: BoardProps) {
  const center = ([row,col]:Point) => [(col-start[1]+.5)*unit, (row-start[0]+.5)*unit];
  const [cx,cy] = center([7,7]);
  return <svg viewBox={`0 0 ${cols*unit} ${rows*unit}`} role="img" aria-label={label}
    data-renju-board={name} data-rows={rows} data-cols={cols} data-cell-size={unit} data-start={start.join(',')}>
    <rect width={cols*unit} height={rows*unit} rx="3" fill="#eee5ca" />
    {scope !== undefined && <rect x={cx-(scope+.5)*unit} y={cy-(scope+.5)*unit} width={(scope*2+1)*unit} height={(scope*2+1)*unit} fill="#d3d9ae" opacity=".65" />}
    {Array.from({length:rows}, (_,n) => <line key={`r${n}`} x1={unit/2} y1={(n+.5)*unit} x2={(cols-.5)*unit} y2={(n+.5)*unit} stroke="#aaa386" strokeWidth=".65" />)}
    {Array.from({length:cols}, (_,n) => <line key={`c${n}`} x1={(n+.5)*unit} y1={unit/2} x2={(n+.5)*unit} y2={(rows-.5)*unit} stroke="#aaa386" strokeWidth=".65" />)}
    {groups.map((points,n) => <polyline key={n} points={points.map(center).map(([x,y])=>`${x},${y+(n%2 ? .34 : -.34)*unit}`).join(' ')}
      fill="none" stroke={n%2?'#7c8770':'#9e874c'} strokeWidth="2" />)}
    {targets.map((at,n) => { const [x,y] = center(at); return <circle key={n} cx={x} cy={y} r={unit*.3} fill="#faf9f0" fillOpacity=".55" stroke="#777e54" strokeWidth="1.2" />; })}
    {stones.map(({at,white,last},n) => {const [x,y]=center(at); return <g key={n} data-stone={white?'white':'black'} data-intersection={at.join(',')} data-last={last||undefined}>
      {last && <circle cx={x} cy={y} r={unit*.43} fill="#d6b35c" />}
      <circle cx={x} cy={y} r={unit*.36} fill={white?WHITE:BLACK} stroke={BLACK} strokeWidth={white?1:.6} />
    </g>;})}
  </svg>;
}
function Pair({name,children}:{name:string;children:ReactNode}) {return <div className="renju-pair" data-comparison={name}>{children}</div>;}
function Panel({caption,children}:{caption:string;children:ReactNode}) {return <figure>{children}<figcaption>{caption}</figcaption></figure>;}
const line = (cols:number[],white=false,last?:number) => cols.map(c=>P([7,c],white,c===last));
const cross = [P([7,5]),P([7,8]),P([6,7]),P([8,7]),P([7,7],false,true)];
const fourCross = [...line([4,5,6]),...[4,5,6].map(r=>P([r,7])),P([7,7],false,true),P([7,3],true)];
function WinningLines() {
  return <Pair name="black-white-win">
    <Panel caption="黒は5個ちょうど">
      <RenjuBoardPicture name="black-five" rows={3} cols={9} unit={16} start={[6,3]} stones={line([5,6,7,8,9],false,9)}
        label="3行9交点の同じ範囲。黒が横にちょうど5個つながり、勝ちです。" />
    </Panel>
    <Panel caption="白は6個でも勝ち">
      <RenjuBoardPicture name="white-six" rows={3} cols={9} unit={16} start={[6,3]} stones={line([4,5,6,7,8,9],true,7)}
        label="左と同じ3行9交点、16単位の間隔。白は横に6個つながっても勝ちです。" />
    </Panel>
  </Pair>;
}
function ForbiddenForks() {
  return <Pair name="double-three-four">
    <Panel caption="三三：活三が2つ">
      <RenjuBoardPicture name="double-three" rows={9} cols={9} unit={16} start={[3,3]} stones={cross}
        groups={[[[7,5],[7,8]],[[6,7],[8,7]]]}
        label="9×9の同じ切り取り。中央の黒1個で横の飛び三と縦の活三が同時にできる三三です。横は列6を埋めると両端が空いた活四になります。五連はありません。黒はここに打てません。" />
    </Panel>
    <Panel caption="四四：四が2つ">
      <RenjuBoardPicture name="double-four" rows={9} cols={9} unit={16} start={[3,3]} stones={fourCross}
        groups={[[[7,4],[7,7]],[[4,7],[7,7]]]}
        label="左と同じ9×9、16単位の間隔。中央の黒1個で横と縦に四が同時にできる四四です。横は左端が白に止められていても、右の列8で五連になる四です。五連はありません。黒はここに打てません。" />
    </Panel>
  </Pair>;
}
function Overline() {
  return <figure className="renju-overline">
    <RenjuBoardPicture name="black-six" rows={3} cols={9} unit={16} start={[6,3]} stones={line([4,5,6,7,8,9],false,7)}
      label="3行9交点。黒が横に6個つながる長連です。ほかの方向に五連はできていないので、黒は打てません。" />
    <figcaption>長連：黒の6個以上のつながり</figcaption>
  </figure>;
}
function OpenThree() {
  return <Pair name="three-four">
    <Panel caption="飛び三も活三です。">
      <RenjuBoardPicture name="broken-three" rows={3} cols={9} unit={16} start={[6,3]} stones={line([5,6,8])} targets={[[7,7]]}
        label="3行9交点。黒3個の間の空点に、禁手にならない黒1個を足すと活四にできます。" />
    </Panel>
    <Panel caption="両端から五連にできます。">
      <RenjuBoardPicture name="straight-four" rows={3} cols={9} unit={16} start={[6,3]} stones={line([5,6,7,8],false,7)} targets={[[7,4],[7,9]]}
        label="左と同じ3行9交点、16単位の間隔。黒1個を足して4連となり、両端のどちらに1個足しても五連になる活四です。移動ではなく、石を追加した前後です。" />
    </Panel>
  </Pair>;
}
function ThreeComparison() {
  return <Pair name="live-dead-three">
    <Panel caption="横も縦も活三なので三三です。">
      <RenjuBoardPicture name="both-threes-live" rows={9} cols={9} unit={16} start={[3,3]} stones={cross}
        label="9×9の同じ切り取り。中央の黒を足すと横の飛び三と縦の三の両方を合法に活四へ伸ばせるので三三です。" />
    </Panel>
    <Panel caption="横は活四に伸びず、三三ではありません。">
      <RenjuBoardPicture name="horizontal-three-blocked" rows={9} cols={9} unit={16} start={[3,3]} stones={[...cross,P([7,4],true),P([7,9],true)]}
        label="左と同じ9×9、同じ16単位の間隔。横の黒3個は両側の白石にふさがれ、活四へ伸ばせません。縦だけが活三なので、この中央の黒は三三ではありません。" />
    </Panel>
  </Pair>;
}
function SameDirectionFour() {
  return <figure className="renju-same-four">
    <RenjuBoardPicture name="same-direction-double-four" rows={3} cols={9} unit={30} start={[6,3]} stones={line([3,5,6,7,9],false,7)} targets={[[7,4],[7,8]]}
      groups={[[[7,3],[7,5],[7,6],[7,7]],[[7,5],[7,6],[7,7],[7,9]]]}
      label="3行9交点。新しく黒を列7へ打つと、列3・5・6・7の四と、列5・6・7・9の四が同じ横の方向にできます。列4または列8でそれぞれ五連となる、同方向の四四です。" />
    <figcaption>同じ横の方向にも、四が2つできます。</figcaption>
  </figure>;
}
function Opening() {
  const b1=P([7,7]), w2=P([8,7],true), b3=P([8,8]), w4=P([7,8],true);
  const first=[b1], second=[b1,w2], third=[b1,w2,b3], fourth=[...third,w4];
  return <div className="renju-opening-grid" data-comparison="opening-ranges">
    <Panel caption="黒1：中央の交点">
      <RenjuBoardPicture name="opening-1" rows={9} cols={9} unit={16} start={[3,3]} stones={first} scope={0} label="中央9×9の同じ範囲。黒1手目は盤の中央の交点に置きます。" />
    </Panel>
    <Panel caption="白2：中央3×3">
      <RenjuBoardPicture name="opening-2" rows={9} cols={9} unit={16} start={[3,3]} stones={second} scope={1} label="左と同じ中央9×9の範囲と16単位の間隔。白2手目は中央3×3の空点に置きます。" />
    </Panel>
    <Panel caption="黒3：中央5×5">
      <RenjuBoardPicture name="opening-3" rows={9} cols={9} unit={16} start={[3,3]} stones={third} scope={2} label="同じ中央9×9の範囲と尺度。黒3手目は中央5×5の空点に置きます。この3個の配置は花月です。" />
    </Panel>
    <Panel caption="白4：花月ではこの1点">
      <RenjuBoardPicture name="opening-4" rows={9} cols={9} unit={16} start={[3,3]} stones={[...third,P([7,8],true,true)]} label="同じ中央9×9の範囲と尺度。この花月の配置に対する白4手目は、中央の黒の右隣の1点です。ほかの配置では候補が変わります。" />
    </Panel>
    <Panel caption="黒5：中央9×9">
      <RenjuBoardPicture name="opening-5" rows={9} cols={9} unit={16} start={[3,3]} stones={[...fourth,P([6,7],false,true)]} scope={4} label="同じ中央9×9の範囲と尺度。黒5手目はこの中央9×9の空点に置きます。" />
    </Panel>
  </div>;
}
export function RenjuRulePaper() {
  return <article className="rule-paper renju-paper">
    <div className="renju-primary" data-renju-primary>
      <section className="renju-win">
        <h2>黒は5個ちょうど、白は5個以上を並べると勝ちです。</h2>
        <p>縦・横・斜めのどれかにつなげます。</p>
        <WinningLines />
        <p>15×15の空いた交点へ、黒から交互に1個ずつ置きます。石は動かしません。</p>
      </section>
      <section className="renju-forbidden">
        <h3>黒だけ打てない形があります。</h3>
        <p>1手で五連ができないとき、活三が2つ以上できる三三、四が2つ以上できる四四、6個以上つながる長連は黒の禁手です。</p>
        <ForbiddenForks /><Overline />
      </section>
    </div>
    <div className="renju-details">
      <section data-renju-detail="three">
        <h3>三は活四に伸ばせる形です。</h3>
        <p>石をあと1個足して五連にできる形を、四といいます。活四は両端のどちらに1個足しても五連になる4連です。</p>
        <p>活三は禁手にならない1手を足して活四にできる3個です。その1手で五連を作る形は活三に数えません。</p>
        <OpenThree />
      </section>
      <section data-renju-detail="dead-three">
        <h3>3個が2組でも、三三とは限りません。</h3>
        <p>白石や盤の端にふさがれたり、伸ばす手が長連・四四・禁手の三三になったりして活四を作れない形は活三に数えません。</p>
        <ThreeComparison />
      </section>
      <section data-renju-detail="four">
        <h3>四四は方向の数ではありません。</h3>
        <p>同じ1手で2つ以上の四ができれば、同じ方向にある四でも禁手です。</p>
        <SameDirectionFour />
        <p>ただし、同じ1手で五連ができれば、ほかの方向に禁手の形ができても黒の勝ちです。</p>
      </section>
      <section data-renju-detail="opening">
        <h3>最初の5手は置ける場所が決まっています。</h3>
        <p>白の4手目は最初の3個の配置ごとに決められた交点から選びます。6手目からは盤全体の空いた交点に置けます。</p>
        <Opening />
        <p>1〜5手目を置いた直後、次の手番の人は黒と白の担当を交換できます。盤上の石は変わりません。交換すると、相手が次の石を置きます。</p>
        <p>全部で5回まで交換できます。続けて2回は交換できません。</p>
      </section>
    </div>
  </article>;
}
